/**
 * Die Einstellungen dieses Werkzeugs — vier Reiter statt einer langen Spalte.
 *
 * Vorher standen Projektion, Kontostand, Umsatzsteuer und Radar untereinander;
 * das Radar allein war laenger als die drei anderen zusammen. Der Zuschnitt in
 * Reiter ist derselbe wie in crm-web (`features/settings/SettingsPage.tsx`) —
 * eine Geste, zwei Apps.
 *
 * Was hier steht, gehoert dem Werkzeug oder der Firma. Alles, was am einzelnen
 * Menschen haengt — Profil, Benachrichtigungen — liegt auf konto.elbklar.com
 * (ADR-0005), erreichbar ueber „Profil“ im Fuss der Seitenleiste.
 *
 * Am Handy (U4: „Am Rechner bearbeiten“) eine Zusammenfassung ohne Eingaben.
 */

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AmRechnerBearbeiten,
  Angaben,
  Card,
  Skeleton,
  Tabs,
  formatDatum,
  formatDatumZeit,
  formatEuro,
  useMobil,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { balanceApi, radarApi, settingsApi } from '@/lib/api';
import { zeitraumText } from '@/lib/steuer';
import { KontostandForm, ProjektionForm, RadarEinstellungen, UmsatzsteuerForm } from './components';

const REITER = [
  { id: 'projektion', label: 'Projektion' },
  { id: 'kontostand', label: 'Kontostand' },
  { id: 'umsatzsteuer', label: 'Umsatzsteuer' },
  { id: 'radar', label: 'Radar' },
] as const;

type ReiterId = (typeof REITER)[number]['id'];

function istReiter(wert: string | null): wert is ReiterId {
  return REITER.some((r) => r.id === wert);
}

export function EinstellungenPage() {
  // Der Reiter steht in der Adresse, damit man auf einen einzelnen verweisen
  // kann — der Hinweis auf der Radar-Seite tut das.
  const [suche, setSuche] = useSearchParams();
  const roh = suche.get('reiter');
  const aktiv: ReiterId = istReiter(roh) ? roh : 'projektion';
  const mobil = useMobil();

  const einstellungen = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });

  const waehlen = (id: string) => {
    if (!istReiter(id)) return;
    // `replace`, sonst sammelt der Zurueck-Knopf jeden Reiterwechsel ein.
    setSuche(id === 'projektion' ? {} : { reiter: id }, { replace: true });
  };

  // Nur zwei der vier Reiter haengen an `settings`; Kontostand und Radar
  // laden selbst.
  const brauchtEinstellungen = aktiv === 'projektion' || aktiv === 'umsatzsteuer';

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Einstellungen"
        subtitle="Die Stellschrauben dieses Werkzeugs. Persönliches — Profil und Benachrichtigungen — liegt im Konto."
      />

      {mobil ? (
        <div className="space-y-4">
          <AmRechnerBearbeiten>Projektion, Kontostand, Umsatzsteuer und Radar lassen sich am Rechner ändern.</AmRechnerBearbeiten>
          <Zusammenfassung />
        </div>
      ) : (
        <Card>
          <Tabs
            items={REITER.map((r) => ({ value: r.id, label: r.label }))}
            value={aktiv}
            onChange={waehlen}
            label="Einstellungen"
          />

          <div className="pt-6">
            {brauchtEinstellungen && einstellungen.isError && (
              <p className="py-8 text-center text-danger">Einstellungen konnten nicht geladen werden.</p>
            )}

            {brauchtEinstellungen && einstellungen.isLoading && (
              <div className="max-w-2xl space-y-3">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10 w-32" />
              </div>
            )}

            {aktiv === 'projektion' && einstellungen.data && <ProjektionForm einstellungen={einstellungen.data} />}
            {aktiv === 'kontostand' && <KontostandForm />}
            {aktiv === 'umsatzsteuer' && einstellungen.data && <UmsatzsteuerForm einstellungen={einstellungen.data} />}
            {aktiv === 'radar' && <RadarEinstellungen />}
          </div>
        </Card>
      )}
    </div>
  );
}

/** Was am Handy statt der Formulare steht: die geltenden Werte, nur zum Lesen. */
function Zusammenfassung() {
  const einstellungen = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });
  const kontostand = useQuery({ queryKey: ['balance-history'], queryFn: balanceApi.list });
  const radar = useQuery({ queryKey: ['radar', 'settings'], queryFn: radarApi.settings, refetchOnWindowFocus: false });

  const e = einstellungen.data;
  const letzter = kontostand.data?.[0];
  const r = radar.data;

  return (
    <>
      <Card title="Projektion">
        {e ? (
          <Angaben
            items={[
              { label: 'Mindestpuffer', value: formatEuro(e.min_buffer), mono: true },
              { label: 'Planungshorizont', value: `${e.horizon_months} Monate` },
              { label: 'Burn-Fenster', value: `${e.burn_lookback_months} Monate` },
            ]}
          />
        ) : (
          <Skeleton className="h-16" />
        )}
      </Card>
      <Card title="Kontostand">
        <Angaben
          items={[
            { label: 'Betrag', value: letzter ? formatEuro(letzter.amount, { cent: true }) : null, mono: true },
            { label: 'Stand vom', value: letzter ? formatDatum(letzter.as_of) : null, mono: true },
          ]}
        />
      </Card>
      <Card title="Umsatzsteuer">
        {e ? (
          <Angaben
            items={[
              { label: 'In der Projektion', value: e.vat_enabled ? 'berücksichtigt' : 'nicht berücksichtigt' },
              { label: 'Voranmeldung', value: e.vat_enabled ? zeitraumText(e.vat_period_months) : null },
              { label: 'Zeitversatz', value: e.vat_enabled ? `${e.vat_delay_months} ${e.vat_delay_months === 1 ? 'Monat' : 'Monate'}` : null },
            ]}
          />
        ) : (
          <Skeleton className="h-16" />
        )}
      </Card>
      <Card title="Radar">
        {r ? (
          <Angaben
            items={[
              { label: 'Prüfung', value: r.enabled ? 'eingeschaltet' : 'ausgeschaltet' },
              { label: 'Suche nach neuen Programmen', value: r.discovery_enabled ? 'eingeschaltet' : 'ausgeschaltet' },
              { label: 'Zuletzt geprüft', value: r.last_monitor_at ? formatDatumZeit(r.last_monitor_at) : null, mono: true },
              { label: 'Zuletzt gesucht', value: r.last_discovery_at ? formatDatumZeit(r.last_discovery_at) : null, mono: true },
            ]}
          />
        ) : (
          <Skeleton className="h-16" />
        )}
      </Card>
    </>
  );
}
